import { Lightning, Storage } from "@lightningjs/sdk";
import { SettingsCarouselItem } from "../layout/SettingsCarouselItem";
import { STORAGE_KEYS, hasBoxPin } from "../../../../../lib/utils";
import { updateAgeRestriction } from "../../../../../api/mutations";

interface SettingsAgeRestrictionTemplateSpec extends Lightning.Component.TemplateSpec {
    Layout: typeof SettingsCarouselItem;
}

export class SettingsAgeRestriction
    extends Lightning.Component<SettingsAgeRestrictionTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsAgeRestrictionTemplateSpec>
{
    readonly SettingsItem = this.getByRef("Layout")!;

    static override _template(): Lightning.Component.Template<SettingsAgeRestrictionTemplateSpec> {
        return {
            collision: true,
            zIndex: 10,
            Layout: {
                type: SettingsCarouselItem,
                title: "settings.ageRestriction"
            }
        };
    }

    override _getFocused() {
        return this.SettingsItem;
    }

    override _setup() {
        this.SettingsItem.patch({ title: "settings.ageRestriction" });
    }

    override _active() {
        this.SettingsItem.patch({ title: "settings.ageRestriction" });
    }

    override _handleEnter() {
        this._open();
        return true;
    }

    override _captureKey(e: KeyboardEvent) {
        if (e.key === "Enter" || e.keyCode === 13) {
            this._open();
            return true;
        }
        return false;
    }

    override _handleRight() {
        this._open();
        return true;
    }

    _handleHover() {
        this.fireAncestors("$listItemHover", this);
    }

    private _hasPin(): boolean {
        // The PIN lives on the box, never in our backend. We deliberately do NOT read
        // user.settings.parentalPin anymore — hasBoxPin() gates on the box only
        // (Android: window.Android.testPinCode present; Linux: pics.pin in Storage).
        const result = hasBoxPin();
        // [JULIA-PIN] Diagnostics for when the console IS available (the on-screen
        // banner covers the no-console case). backendPinIgnored is logged only to show
        // it is no longer used for the decision.
        try {
            const user = Storage.get(STORAGE_KEYS.user) as any;
            const android: any = (window as any).Android;
            console.info(
                "[JULIA-PIN] _hasPin: " +
                    JSON.stringify({
                        os: Storage.get(STORAGE_KEYS.posttvOS) || null,
                        result,
                        storagePin: Storage.get(STORAGE_KEYS.parentalPin) ?? null,
                        hasTestPinCode: !!(android && typeof android.testPinCode === "function"),
                        androidKeys: android ? Object.keys(android) : null,
                        backendPinIgnored: user?.settings?.parentalPin ?? null
                    })
            );
        } catch (e) {
            console.error("[JULIA-PIN] _hasPin diagnostics failed:", e);
        }
        return result;
    }

    private _findWidget(name: string): any {
        let current: any = this;
        let depth = 0;
        while (current && depth < 20) {
            try {
                if (current.getByRef) {
                    const widgets = current.getByRef("Widgets");
                    if (widgets) {
                        const widget = widgets.getByRef(name);
                        if (widget) return widget;
                    }
                }
            } catch (e) {
                // continue
            }
            current = current.parent;
            depth++;
            if (!current || current === this.application || current === this.stage) {
                break;
            }
        }
        return null;
    }

    _open() {
        console.log("[SettingsAgeRestriction] _open called");
        if (!this._hasPin()) {
            // No PIN on the box yet. The PIN is owned by the STB firmware (Netgem native
            // UI), not by this app, so there's nothing to verify against and we can't gate
            // an age change. Do nothing until a box PIN exists.
            console.log("[SettingsAgeRestriction] No PIN set on box — cannot change age");
            return;
        }

        const pinInput = this._findWidget("PinInput");
        if (!pinInput) {
            console.error("[SettingsAgeRestriction] PinInput widget not found");
            return;
        }

        console.info("[JULIA-AgeR] _open: PinInput found, opening verify; hash:", window.location.hash);
        pinInput.open(
            () => {
                console.log("[SettingsAgeRestriction] PIN verified, opening AgeSelector");
                this._openAgeSelector();
            },
            () => {
                console.log("[SettingsAgeRestriction] PIN verify cancelled");
            },
            "verify"
        );
    }

    _openAgeSelector() {
        const ageSelector = this._findWidget("AgeSelector");
        if (!ageSelector) {
            console.error("[SettingsAgeRestriction] AgeSelector widget not found");
            return;
        }

        const user = Storage.get(STORAGE_KEYS.user) as any;
        const currentAge =
            user?.settings?.parentalAgeRestriction ||
            (Storage.get(STORAGE_KEYS.parentalAgeRestriction) as string | undefined);

        ageSelector.open(
            (age: string) => {
                console.log("[SettingsAgeRestriction] Age set:", age);
                void this._save(age);
            },
            currentAge,
            undefined
        );
    }

    async _save(age: string) {
        // Age is our own setting and must survive even where localStorage doesn't persist
        // across restarts (PostTV Android). Persist to our backend (durable source) and
        // write-through to Storage (fast local read). We never touch the STB (Netgem
        // settings/set is unsupported — error 8).
        Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);

        // The read path prefers user.settings over Storage, so update the in-memory user
        // too — otherwise the new value wouldn't take effect until the next refresh/boot.
        const user = Storage.get(STORAGE_KEYS.user) as any;
        if (user?.settings) {
            user.settings.parentalAgeRestriction = age;
            Storage.set(STORAGE_KEYS.user, user);
        }

        try {
            await updateAgeRestriction(age);
        } catch (e) {
            console.error("[SettingsAgeRestriction] backend age save failed (kept local):", e);
        }
    }
}
